import React, { useState, useEffect, useMemo } from 'react';

import { dataGridPremiumColumns } from '../../utils/helper';
import { AutoCompleteSelectUI, DataGridIdentifiers, DatagridPremiumUi, FormLabelUI } from '../Interface';
import taskListService from '../../service/taskList.service';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';

export const ProjectTaskReports = ({
  refetch,
  values,
  viewField,
  operatorsDisabled,
  rowSelection,
  autocompleteOptions,
  autocompleteData,
}) => {
  const { params } = useParam();
  const { refetchTable, setRefetchTable } = refetch;
  const { setSelectedValues } = values;
  const { rowSelectionModel, setRowSelectionModel } = rowSelection;

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'name', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  const getList = (gridState) => {
    taskListService.getGridTasks(gridState).then((data) => {
      setReports(data);
    });
  };

  const ResponsibleFilterInput = ({ item, applyValue }) => {
    return (
      <AutoCompleteSelectUI
        label={<FormLabelUI text="Felelős" />}
        variant="standard"
        dataset={autocompleteOptions.responsible}
        getOptionLabel={(o) => o.label}
        value={item.value ?? null}
        onChange={(_, value) =>
          applyValue({
            ...item,
            value, // teljes option
          })
        }
        //renderInput={(params) => <TextField {...params} variant="standard" />}
      />
    );
  };

  const responsibleFilterOperators = [
    {
      label: 'Egyenlő',
      value: 'is',
      InputComponent: ResponsibleFilterInput,
      getApplyFilterFn: (item) => {
        if (!item.value) return null;

        return (params) =>
          params.row.responsibleType === item.value.type &&
          (params.row.responsiblePersonId ??
            params.row.responsibleUserGroupId ??
            params.row.responsibleCompanyId ??
            params.row.responsibleWorkerId) === item.value.value;
      },
    },
    {
      label: 'Nem egyenlő',
      value: 'not',
      InputComponent: ResponsibleFilterInput,
      getApplyFilterFn: (item) => {
        if (!item.value) return null;

        return (params) =>
          !(
            params.row.responsibleType === item.value.type &&
            (params.row.responsiblePersonId ??
              params.row.responsibleUserGroupId ??
              params.row.responsibleCompanyId ??
              params.row.responsibleWorkerId) === item.value.value
          );
      },
    },
  ];

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Megnevezés',
        type: 'string',
      },
      {
        field: 'projectName',
        type: 'string',
        headerName: 'Projekt',
        renderCell: (data) => {
          return autocompleteOptions?.project?.find((project) => project.value === data.row?.lineId)?.label;
        },
      },
      {
        field: 'typeId',
        type: 'singleSelect',
        headerName: 'Típus',
        renderCell: (data) => {
          return data.row.type?.itemName;
        },
        valueOptions: autocompleteOptions.type,
      },
      {
        field: 'statusId',
        type: 'singleSelect',
        headerName: 'Státusz',
        renderCell: (data) => {
          return data.row.status?.itemName;
        },
        valueOptions: autocompleteOptions.status,
      },
      {
        field: 'preparedness',
        headerName: 'Készültség',
        type: 'number',
      },
      {
        field: 'startDate',
        headerName: 'Kezdő dátum',
        type: 'date',
        valueGetter: (_data, row) => {
          return row.startDate ? new Date(row.startDate) : '';
        },
      },
      {
        field: 'duration',
        headerName: 'Időintervallum (nap)',
        type: 'number',
      },
      {
        field: 'responsible',
        type: 'singleSelect',
        headerName: 'Felelős',
        renderCell: (params) => {
          const data = params.row;
          const relevantValue =
            data.responsiblePersonId ??
            data.responsibleUserGroupId ??
            data.responsibleCompanyId ??
            data.responsibleWorkerId;

          const option = autocompleteOptions?.responsible?.find(
            (item) => item.value === relevantValue && item.type === data?.responsibleType
          );

          return (
            <div>
              {option?.icon ? <span style={{ display: 'inline-flex', marginRight: 8 }}>{option.icon}</span> : null}
              {option?.label}
            </div>
          );
        },
        valueOptions: autocompleteOptions.responsible,
        filterOperators: responsibleFilterOperators,
        headerFilterOperators: responsibleFilterOperators,
      },
      {
        field: 'userGroupId',
        type: 'singleSelect',
        headerName: 'Felelősök (csoport)',
        renderCell: (data) => {
          return data.row.userGroup?.name;
        },
        valueOptions: autocompleteOptions.userGroup,
      },
      {
        field: 'userId',
        type: 'singleSelect',
        headerName: 'Tulajdonos',
        renderCell: (data) => {
          return data.row.user?.nickName;
        },
        valueOptions: autocompleteOptions.user,
      },
      {
        field: 'deadLine',
        headerName: 'Határidő',
        type: 'dateTime',
        valueGetter: (_data, row) => {
          return row.deadLine ? new Date(row.deadLine) : null;
        },
      },
      {
        field: 'projectOrderNumber',
        headerName: getProjectOrderNumberName(params),
        type: 'string',
        sortable: false,
        renderCell: (data) => {
          return autocompleteData?.project?.find((item) => item.projectId === data.row.lineId)?.orderNumber;
        },
      },
      ...(params.TASKLIST_SHOW_TASKRATIO_FIELD === 'Y'
        ? [
            {
              field: 'taskRatio',
              headerName: 'Feladat arány',
              type: 'number',
              sortable: false,
            },
          ]
        : []),
    ]);
  }, [autocompleteOptions]);

  return (
    <DatagridPremiumUi
      datagridIdentifier={DataGridIdentifiers.projectTaskLists}
      datagridInitialState={initialState}
      dataLoader={getList}
      rows={reports.rows}
      refetch={{ refetchTable, setRefetchTable }}
      rowCount={reports.rowCount}
      columns={[...columns]}
      onRowClick={(rowData) => {
        if (!viewField) return;
        setSelectedValues(rowData.row);
      }}
      paginationAndfilterMode="server"
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!viewField}
    />
  );
};
